export type TabType =
  | "attendance"
  | "my-pulse"
  | "leave"
  | "leave-approvals"
  | "salary-advance"
  | "advance-approvals"
  | "live-board"
  | "payroll"
  | "team-directory"
  | "hr-settings"
  | "admin-team"
  | "tasks"
  | "welfare";

export type ModuleType =
  | "attendance"
  | "my-stats"
  | "leave"
  | "leave-approvals"
  | "salary-advance"
  | "advance-approvals"
  | "live-board"
  | "payroll"
  | "team-directory"
  | "hr-settings"
  | "tasks"
  | "welfare";

export type AttendanceLocation = {
  displayName: string;
  kind: "เช็คอิน" | "เช็คเอาท์";
  lat: number;
  lng: number;
  accuracy?: number | null;
  recordedAt: string | Date;
};

export const getIsExecutive = () => {
  if (typeof window === "undefined") return false;
  if (window.location.pathname === "/admin" || window.location.pathname.startsWith("/admin/")) return true;
  try {
    const url = new URL(window.location.href);
    const role = url.searchParams.get("role");
    if (role === "admin" || role === "ceo") return true;
    let module = url.searchParams.get("module");
    const liffState = url.searchParams.get("liff.state");
    if (liffState) {
      const nested = new URLSearchParams(decodeURIComponent(liffState).replace(/^\?/, ""));
      if (nested.get("role") === "admin" || nested.get("role") === "ceo") return true;
      if (nested.get("module")) module = nested.get("module");
    }
    if (
      module &&
      ["live-board", "advance-approvals", "payroll", "team-directory", "hr-settings"].includes(module)
    ) {
      return true;
    }
  } catch {
    // fallback
  }
  return false;
};

export const executiveEntry = getIsExecutive();
export const EMPLOYEE_LIFF_ID =
  import.meta.env.VITE_LIFF_STAFF_ID || import.meta.env.VITE_LIFF_ID || "2011801437-tGPmqkBl";
export const EXECUTIVE_LIFF_ID =
  import.meta.env.VITE_LIFF_CEO_ID || "2011801437-GGWMtfZN";
export const liffId = executiveEntry ? EXECUTIVE_LIFF_ID : EMPLOYEE_LIFF_ID;

export async function ensureLiffSdk() {
  if (window.liff) return;
  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="static.line-scdn.net/liff"]'
    );
    existing?.remove();
    const script = document.createElement("script");
    const timer = window.setTimeout(
      () => reject(new Error("โหลด LINE LIFF SDK ไม่สำเร็จ")),
      10000
    );
    script.addEventListener(
      "load",
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
    script.addEventListener(
      "error",
      () => {
        window.clearTimeout(timer);
        reject(new Error("เบราว์เซอร์บล็อก LINE LIFF SDK"));
      },
      { once: true }
    );
    script.src = "https://static.line-scdn.net/liff/edge/2/sdk.js";
    script.async = true;
    document.head.appendChild(script);
  });
  if (!window.liff) throw new Error("ไม่พบ LINE LIFF SDK หลังโหลดเสร็จ");
}

export function bangkokWorkDate(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function thaiTime(value: string | Date) {
  return new Date(value).toLocaleString("th-TH", {
    timeZone: "Asia/Bangkok",
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function thaiTimeShort(value: string | Date) {
  return new Date(value).toLocaleTimeString("th-TH", {
    timeZone: "Asia/Bangkok",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function workDuration(start: string | Date, end?: string | Date | null) {
  if (!end) return "กำลังปฏิบัติงาน";
  const minutes = Math.max(
    0,
    Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000)
  );
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours} ชม. ${mins} นาที`;
}

export function fiscalMonths(fiscalYear: number) {
  const labels = [
    "ต.ค.",
    "พ.ย.",
    "ธ.ค.",
    "ม.ค.",
    "ก.พ.",
    "มี.ค.",
    "เม.ย.",
    "พ.ค.",
    "มิ.ย.",
    "ก.ค.",
    "ส.ค.",
    "ก.ย.",
  ];
  return labels.map((label, index) => {
    const month = index < 3 ? index + 10 : index - 2;
    const year = index < 3 ? fiscalYear - 1 : fiscalYear;
    return { label, key: `${year}-${String(month).padStart(2, "0")}` };
  });
}

export const moduleToTab: Record<ModuleType, TabType> = {
  attendance: "attendance",
  "my-stats": "my-pulse",
  leave: "leave",
  "leave-approvals": "leave-approvals",
  "salary-advance": "salary-advance",
  "advance-approvals": "advance-approvals",
  "live-board": "live-board",
  payroll: "payroll",
  "team-directory": "team-directory",
  "hr-settings": "hr-settings",
  tasks: "tasks",
  welfare: "welfare",
};

export const EMPLOYEE_MODULES: readonly ModuleType[] = [
  "attendance",
  "my-stats",
  "salary-advance",
  "tasks",
  "welfare",
];

export const EXECUTIVE_MODULES: readonly ModuleType[] = [
  "live-board",
  "advance-approvals",
  "payroll",
  "team-directory",
  "hr-settings",
  "tasks",
  "welfare",
];

export function isModuleAllowed(module: ModuleType, executive: boolean) {
  return (executive ? EXECUTIVE_MODULES : EMPLOYEE_MODULES).includes(module);
}

export function readDirectEntry() {
  if (typeof window === "undefined")
    return {
      module: null as ModuleType | null,
      action: null as "checkin" | "checkout" | null,
    };
  const url = new URL(window.location.href);
  let module = url.searchParams.get("module") as ModuleType | null;
  let action = url.searchParams.get("action") as "checkin" | "checkout" | null;
  const liffState = url.searchParams.get("liff.state");
  if (liffState) {
    try {
      const nested = new URLSearchParams(
        decodeURIComponent(liffState).replace(/^\?/, "")
      );
      module ||= nested.get("module") as ModuleType | null;
      action ||= nested.get("action") as "checkin" | "checkout" | null;
    } catch {
      // Fall back to the regular query string.
    }
  }
  if (!module || !(module in moduleToTab) || !isModuleAllowed(module, getIsExecutive())) module = null;
  if (action !== "checkin" && action !== "checkout") action = null;
  return { module, action };
}

export interface SystemFeatureFlags {
  enableLeaveSystem: boolean;
  enableSalaryAdvance: boolean;
  enableOvertime: boolean;
  enablePerformanceReview: boolean;
  enableGPSLocation: boolean;
}

export const defaultFeatureFlags: SystemFeatureFlags = {
  enableLeaveSystem: false,
  enableSalaryAdvance: true,
  enableOvertime: true,
  enablePerformanceReview: true,
  enableGPSLocation: true,
};

export function parseSystemFlags(raw?: Record<string, string>): SystemFeatureFlags {
  if (!raw) return defaultFeatureFlags;
  return {
    enableLeaveSystem: false,
    enableSalaryAdvance: raw.enableSalaryAdvance !== "false",
    enableOvertime: raw.enableOvertime !== "false",
    enablePerformanceReview: raw.enablePerformanceReview !== "false",
    enableGPSLocation: raw.enableGPSLocation !== "false",
  };
}
